import client from "./config";
import { API_HOST } from "../BaseUrl";
import { generateHMAC } from "../../Utils/hmac";
import api from "./sessionExpiryAxios";
import { getClinicAccessToken, getClinicDetails } from "../../Utils/clinicAuth";

/**
 * Fetch sim users by company with filtering and pagination
 * @param {Object} filters - Filter options
 * @param {string} filters.status - User status: 'new', 'contacted', 'consultation_scheduled', or ''
 * @param {boolean} filters.is_high_intent - Filter by high intent users
 * @param {number} filters.page - Page number for pagination (minimum: 1)
 * @param {number} filters.limit - Number of users per page (default: 8, max: 100)
 * @returns {Promise} Response containing users data and pagination info
 */
export const getSimUsersByCompany = async (filters = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();
            // Prepare request body
            const requestBody = {
                ...(filters.status ? { status: filters.status } : {}),
                // status: filters.status || "",
                is_high_intent: filters.is_high_intent || false,
                page: filters.page || 1,
                limit: filters.limit || 8,
            };

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            // if (!authToken) {
            //     reject(new Error("Access token is required"));
            //     return;
            // }

            // Make the API request with required headers
            const response = await api.post(
                `${API_HOST}get-sim-users-by-company`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error fetching users by company:", error);
            reject(error);
        }
    });
};

/**
 * Authenticates a user with email and password.
 * @param {string} email - The user's email address.
 * @param {string} password - The user's password.
 * @returns {Promise<Object>} - A promise that resolves with the login response data.
 */
export const loginUser = async (email, password) => {
    try {
        const { token: hmacSignature, timestamp } = await generateHMAC();
        const response = await client.post(`${API_HOST}tp-app/login`, {
            authenticate_email: email,
            password: password,
        }, {
            headers: {
                "x-hmac": hmacSignature,
                "x-rt": timestamp,
                "Content-Type": "application/json",
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Sends a forgot password request for the given email.
 * @param {string} email - The user's email address.
 * @returns {Promise<Object>} - A promise that resolves with the response data.
 */
export const forgotPassword = async (email) => {
    try {
        const { token: hmacSignature, timestamp } = await generateHMAC();
        const response = await client.post(`${API_HOST}tp-app/forgot-password`, {
            authenticate_email: email,
        }, {
            headers: {
                "x-hmac": hmacSignature,
                "x-rt": timestamp,
                "Content-Type": "application/json",
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Verifies the OTP for a given email.
 * @param {string} email - The user's email address.
 * @param {string} otp - The OTP to verify.
 * @returns {Promise<Object>} - A promise that resolves with the response data.
 */
export const verifyOtp = async (email, otp) => {
    try {
        const { token: hmacSignature, timestamp } = await generateHMAC();
        const response = await client.post(`${API_HOST}tp-app/verify-otp`, {
            authenticate_email: email,
            otp: otp,
        }, {
            headers: {
                "x-hmac": hmacSignature,
                "x-rt": timestamp,
                "Content-Type": "application/json",
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Resets the user's password.
 * @param {string} email - The user's email address.
 * @param {string} newPassword - The new password.
 * @param {string} confirmPassword - The confirmed new password.
 * @returns {Promise<Object>} - A promise that resolves with the response data.
 */
export const resetPassword = async (email, newPassword, confirmPassword) => {
    try {
        const { token: hmacSignature, timestamp } = await generateHMAC();
        const response = await client.post(`${API_HOST}tp-app/reset-password`, {
            authenticate_email: email,
            password: newPassword,
            confirm_password: confirmPassword,
        }, {
            headers: {
                "x-hmac": hmacSignature,
                "x-rt": timestamp,
                "Content-Type": "application/json",
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch Claraview admin user details
 * @returns {Promise} Response containing admin user information
 */
export const getClaraviewAdminDetails = async () => {
    return new Promise(async (resolve, reject) => {
        try {
            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Make the API request with required headers
            const response = await api.get(
                `${API_HOST}claraview/admin`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error fetching Claraview admin details:", error);
            reject(error);
        }
    });
};

/**
 * Update Claraview admin user details
 * @param {Object} updateData - Data to update
 * @param {string} updateData.doctor_name - Doctor's full name (optional)
 * @param {string} updateData.practise_name - Practice name (optional)
 * @param {Array<string>} updateData.email - Array of email addresses (optional)
 * @param {string} updateData.phone_number - Phone number (optional)
 * @param {string} updateData.address - Address (optional)
 * @param {string} updateData.company - Company identifier (required)
 * @returns {Promise} Response containing updated admin details
 */
export const updateClaraviewAdminDetails = async (updateData = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate that company is provided
            if (!updateData.company) {
                reject(new Error("Company is required"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Prepare request body with only provided fields
            const requestBody = {
                ...(updateData.doctor_name && { doctor_name: updateData.doctor_name }),
                ...(updateData.practise_name && { practise_name: updateData.practise_name }),
                ...(updateData.email && { email: Array.isArray(updateData.email) ? updateData.email : [updateData.email] }),
                ...(updateData.phone_number && { phone_number: updateData.phone_number }),
                ...(updateData.address && { address: updateData.address }),
                company: updateData.company, // Always include company
            };

            // Make the API request with required headers
            const response = await api.put(
                `${API_HOST}claraview/admin-update`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error updating Claraview admin details:", error);
            reject(error);
        }
    });
};

/**
 * Send a custom email to a patient with Claraview template
 * @param {Object} emailData - Email configuration object
 * @param {string} emailData.email - Recipient email address (required)
 * @param {string} emailData.subject - Email subject, 1-200 characters (required)
 * @param {string} emailData.payload - Email body, 1-10000 characters, supports \n for line breaks (required)
 * @param {string} emailData.template - Email template identifier (optional, defaults to 'custom')
 * @returns {Promise} Response confirming email was sent
 */
export const sendEmail = async (emailData = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate required fields
            if (!emailData.email) {
                reject(new Error("Email is required"));
                return;
            }

            if (!emailData.subject) {
                reject(new Error("Subject is required"));
                return;
            }

            if (!emailData.payload) {
                reject(new Error("Message payload is required"));
                return;
            }

            // Validate subject length
            if (emailData.subject.length < 1 || emailData.subject.length > 200) {
                reject(new Error("Subject must be between 1 and 200 characters"));
                return;
            }

            // Validate payload length
            if (emailData.payload.length < 1 || emailData.payload.length > 10000) {
                reject(new Error("Payload must be between 1 and 10000 characters"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Prepare request body
            const requestBody = {
                email: emailData.email,
                subject: emailData.subject,
                payload: emailData.payload,
                ...(emailData.template && { template: emailData.template }),
            };

            // Make the API request with required headers
            const response = await api.post(
                `${API_HOST}claraview/send-email`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error sending email:", error);
            reject(error);
        }
    });
};

/**
 * Create a new note for a patient/user
 * @param {Object} noteData - Note data object
 * @param {string} noteData.text - Note content, 1-5000 characters (required)
 * @param {string} noteData.uid - User ID, 1-100 characters (required)
 * @returns {Promise} Response with created note data
 */
export const createNote = async (noteData = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate required fields
            if (!noteData.text) {
                reject(new Error("Note text is required"));
                return;
            }

            if (!noteData.uid) {
                reject(new Error("User ID is required"));
                return;
            }

            // Validate text length
            if (noteData.text.length < 1 || noteData.text.length > 5000) {
                reject(new Error("Note text must be between 1 and 5000 characters"));
                return;
            }

            // Validate uid length
            if (noteData.uid.length < 1 || noteData.uid.length > 100) {
                reject(new Error("User ID must be between 1 and 100 characters"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Prepare request body
            const requestBody = {
                text: noteData.text.trim(),
                uid: noteData.uid,
            };

            // Make the API request with required headers
            const response = await api.post(
                `${API_HOST}claraview/notes/create`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 201 || response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error creating note:", error);
            reject(error);
        }
    });
};

/**
 * Get all notes for a specific user
 * @param {string} uid - User ID (required, 1-100 characters)
 * @returns {Promise} Response with array of notes
 */
export const getUserNotes = async (uid) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate uid
            if (!uid) {
                reject(new Error("User ID is required"));
                return;
            }

            if (uid.length < 1 || uid.length > 100) {
                reject(new Error("User ID must be between 1 and 100 characters"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Make the API request with required headers
            const response = await api.get(
                `${API_HOST}claraview/notes/user/list/${uid}`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error fetching user notes:", error);
            reject(error);
        }
    });
};

/**
 * Update an existing note
 * @param {string} noteId - Note MongoDB ObjectId (required)
 * @param {Object} updateData - Update data object
 * @param {string} updateData.text - Updated note content, 1-5000 characters (required)
 * @returns {Promise} Response with updated note data
 */
export const updateNote = async (noteId, updateData = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate noteId
            if (!noteId) {
                reject(new Error("Note ID is required"));
                return;
            }

            // Validate text
            if (!updateData.text) {
                reject(new Error("Note text is required"));
                return;
            }

            // Validate text length
            if (updateData.text.length < 1 || updateData.text.length > 5000) {
                reject(new Error("Note text must be between 1 and 5000 characters"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Prepare request body
            const requestBody = {
                text: updateData.text.trim(),
            };

            // Make the API request with required headers
            const response = await api.put(
                `${API_HOST}claraview/notes/update/${noteId}`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error updating note:", error);
            reject(error);
        }
    });
};

/**
 * Delete a note
 * @param {string} noteId - Note MongoDB ObjectId (required)
 * @returns {Promise} Response confirming deletion
 */
export const deleteNote = async (noteId) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate noteId
            if (!noteId) {
                reject(new Error("Note ID is required"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Make the API request with required headers
            const response = await api.delete(
                `${API_HOST}claraview/notes/delete/${noteId}`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error deleting note:", error);
            reject(error);
        }
    });
};

/**
 * Fetch smile preview images (before and after) for a patient
 * @param {string} folderName - Patient UID (required)
 * @returns {Promise} Response with cropData (before) and correctionData (after)
 */
export const getSmilePreviewImages = async (folderName) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate folderName (UID)
            if (!folderName) {
                reject(new Error("Patient UID (folderName) is required"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                const error = new Error("Token is invalid");
                error.code = "INVALID_TOKEN";
                reject(error);
                return;
            }

            const clinicDetails = getClinicDetails() || {};

            // Make the API request with required headers
            const response = await api.get(
                `${API_HOST}combo-admin/${folderName}/${clinicDetails.company}`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error fetching smile preview images:", error);

            // Check for token-related errors
            if (error?.response?.status === 401 || error?.code === "INVALID_TOKEN") {
                const tokenError = new Error("Token is invalid");
                tokenError.code = "INVALID_TOKEN";
                reject(tokenError);
            } else {
                reject(error);
            }
        }
    });
};

/**
 * Update user status
 * @param {string} userId - User UID (patient_UUID) for status update (required)
 * @param {string} status - Status to set: 'new', 'contacted', or 'consultation_scheduled' (required)
 * @returns {Promise} Response confirming status update
 */
export const updateUserStatus = async (userId, status) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Validate userId
            if (!userId) {
                reject(new Error("User UID is required"));
                return;
            }

            // Validate status
            const validStatuses = ['new', 'contacted', 'consultation_scheduled'];
            if (!status || !validStatuses.includes(status)) {
                reject(new Error("Invalid status. Must be: new, contacted, or consultation_scheduled"));
                return;
            }

            // Generate HMAC and timestamp
            const { token: hmacSignature, timestamp } = await generateHMAC();

            // Get JWT token from localStorage
            const authToken = getClinicAccessToken();

            if (!authToken) {
                reject(new Error("Access token is required"));
                return;
            }

            // Make the API request with required headers
            const response = await api.put(
                `${API_HOST}update-user-status/${userId}`,
                { status: status },
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "x-hmac": hmacSignature,
                        "x-rt": timestamp,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                resolve(response.data);
            } else {
                reject(response);
            }
        } catch (error) {
            console.error("Error updating user status:", error);

            // Handle token validation errors
            if (error?.code === 'INVALID_TOKEN' || error?.response?.status === 401) {
                const tokenError = new Error("Token is invalid");
                tokenError.code = "INVALID_TOKEN";
                reject(tokenError);
            } else {
                reject(error);
            }
        }
    });
};
